import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';
import { useAuthStore } from '../lib/auth.js';
import TotpInput from '../components/TotpInput.jsx';

export default function LoginPage() {
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [totpLayer, setTotpLayer] = useState(null);
  const [busy, setBusy] = useState(false);
  const setAuth = useAuthStore(s => s.setAuth);
  const nav = useNavigate();

  async function doLogin(codeOverride) {
    if (busy) return;
    setBusy(true);
    try {
      const body = { username, password };
      const code = codeOverride ?? totpCode;
      if (code) body.totp_code = code;
      const res = await api.login(body);

      if (res.require_totp) {
        setNeedTotp(true);
        setTotpLayer(res.kind || 'L1');
        toast('Yêu cầu mã 6 số L1 từ app Authenticator', { icon: '🔐' });
        return;
      }

      setAuth({
        accessToken: res.access_token,
        refreshToken: res.refresh_token,
        user: res.user,
      });
      toast.success(`Xin chào ${res.user.display_name}`);
      // M144a (2026-05-12): manager cũng vào /admin (giống admin)
      nav((res.user?.role === 'admin' || res.user?.role === 'manager') ? '/admin' : '/');
    } catch (err) {
      const msg = err.data?.error || err.message;
      if (err.status === 423) {
        toast.error(`Tài khoản bị khoá đến ${new Date(err.data.locked_until).toLocaleTimeString('vi-VN')}`);
      } else {
        toast.error(msg + (err.data?.attempts_remaining != null ? ` (còn ${err.data.attempts_remaining} lần)` : ''));
      }
    } finally {
      setBusy(false);
    }
  }

  function submitForm(e) {
    e.preventDefault();
    doLogin();
  }

  return (
    <div className="min-h-full flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 to-brand-50">
      <form onSubmit={submitForm} className="max-w-sm w-full bg-white rounded-2xl shadow-lg border border-slate-200 p-7 space-y-4">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-brand-700">Hello Con Đào Tạo</h1>
          <p className="text-sm text-slate-500 mt-1">Đăng nhập</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">Tên đăng nhập</label>
          <input
            type="text"
            value={username}
            onChange={e => setUsername(e.target.value)}
            disabled={needTotp}
            required
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">Mật khẩu</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={needTotp}
            required
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
          />
        </div>

        {needTotp && (
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Mã {totpLayer} (6 số từ Authenticator)
            </label>
            <TotpInput
              value={totpCode}
              onChange={setTotpCode}
              onSubmit={(code) => doLogin(code)}
              autoFocus
              disabled={busy}
              testId="login-totp-input"
              className="mt-1"
            />
          </div>
        )}

        <button
          type="submit"
          disabled={busy || (needTotp && totpCode.length !== 6)}
          className="w-full py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {busy ? 'Đang xử lý…' : needTotp ? 'Xác nhận' : 'Đăng nhập'}
        </button>

        {needTotp && (
          <button
            type="button"
            onClick={() => { setNeedTotp(false); setTotpCode(''); }}
            className="w-full py-2 text-sm text-slate-500 hover:text-slate-700"
          >
            ← Quay lại
          </button>
        )}
      </form>
    </div>
  );
}
